import { isRedirect, redirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { getPublicEnv } from "#/modules/util.instrumentation/public-env";
import { isServerNewer } from "#/modules/util.instrumentation/version-skew";
import { z } from "zod/v4";

// Custom redirect option; a server-fn redirect ships its options over the wire, so this survives.
const SKEW_OPTION = "versionSkewTo";
const RELOADED_AT_KEY = "version-skew-reloaded-at";
const RELOAD_COOLDOWN_MS = 10_000;

type SkewRedirectOptions = { [SKEW_OPTION]?: string };
const skewRedirectOptionsSchema = z.object({ [SKEW_OPTION]: z.string().optional() }).catch({});

let reloading = false;

// Skew guard, hard half (docs/deploy-runbook.md): reload tabs on a build older than the server's.
export const versionSkewMiddleware = createMiddleware({ type: "function" })
  .client(async ({ next }) => {
    const clientVersion = import.meta.env.SSR ? undefined : getPublicEnv().APP_VERSION;
    try {
      return await next({ sendContext: { clientVersion } });
    } catch (error) {
      const serverVersion = skewVersionOf(error);
      if (import.meta.env.SSR || !serverVersion) throw error;
      if (!reloading) {
        if (reloadedRecently()) {
          throw new Error(
            `Version skew: still on ${clientVersion} after reloading for ${serverVersion}`,
          );
        }
        reloading = true;
        window.location.reload();
      }
      return new Promise<never>(() => {});
    }
  })
  .server(async ({ next, context }) => {
    const serverVersion = process.env.APP_VERSION;
    if (serverVersion && isServerNewer(serverVersion, context.clientVersion)) {
      throw skewRedirect(serverVersion);
    }
    return next();
  });

/** The redirect the client half reloads on; also thrown for ids this build no longer has. */
export function skewRedirect(serverVersion: string) {
  const options: SkewRedirectOptions = { [SKEW_OPTION]: serverVersion };
  return redirect({ href: "/", reloadDocument: true, ...options });
}

function skewVersionOf(cause: unknown): string | undefined {
  if (!isRedirect(cause)) return undefined;
  return skewRedirectOptionsSchema.parse(cause.options)[SKEW_OPTION];
}

// A reload that still boots the old build (e.g. cached HTML) would otherwise reload forever.
function reloadedRecently() {
  try {
    const reloadedAt = Number(sessionStorage.getItem(RELOADED_AT_KEY));
    if (Date.now() - reloadedAt < RELOAD_COOLDOWN_MS) return true;
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: one uncontrolled reload beats a crash in stale code.
  }
  return false;
}
